/**
 * factions.ts — Faction definitions and reputation tiers.
 *
 * 3 factions with reputation tracking, standing tiers, and titles.
 */

import type { ClassId } from './types';

// ─── Types ──────────────────────────────────────────────────────

export type FactionId = 'winterguard' | 'resistance' | 'ancient_powers';

export type FactionStanding = 'Hostile' | 'Unfriendly' | 'Neutral' | 'Friendly' | 'Revered';

export interface FactionDefinition {
  id: FactionId;
  name: string;
  symbol: string;
  color: 'frostBlue' | 'green' | 'magenta';
  leader: string;
  leaderId: string;
  hq: string;
  hqLocationId: string;
  description: string;
  /** Titles awarded at each standing tier. */
  titles: Partial<Record<FactionStanding, string>>;
  /** Classes that get a starting reputation bonus. */
  classAffinity?: Partial<Record<ClassId, number>>;
}

// ─── Faction Data ───────────────────────────────────────────────

export const FACTIONS: FactionDefinition[] = [
  {
    id: 'winterguard',
    name: 'Winterguard Council',
    symbol: '⛊',
    color: 'frostBlue',
    leader: 'Commander Aldric',
    leaderId: 'aldric',
    hq: 'Gallowcrest Citadel',
    hqLocationId: 'gallowcrest',
    description: 'The ruling military council of the Greyveil. They maintain order through discipline and strength, but their methods grow harsher as the crisis deepens.',
    titles: {
      Friendly: 'Friend of the Watch',
      Revered: 'Hero of the Watch',
    },
    classAffinity: {
      frostblade_fighter: 5,
      dragon_knight: 5,
    },
  },
  {
    id: 'resistance',
    name: 'The Resistance',
    symbol: '⚔',
    color: 'green',
    leader: 'Theron',
    leaderId: 'theron',
    hq: 'Shadow Quarter',
    hqLocationId: 'war-camp',
    description: 'A loose alliance of rebels, displaced villagers, and former soldiers who believe the Council has failed. They fight for the common people.',
    titles: {
      Friendly: 'Shadow Agent',
      Revered: 'Champion of the People',
    },
    classAffinity: {
      necromancer_hunter: 5,
      runepriest: 5,
    },
  },
  {
    id: 'ancient_powers',
    name: 'Ancient Powers',
    symbol: '✦',
    color: 'magenta',
    leader: 'Valdris',
    leaderId: 'valdris',
    hq: 'Ancient Temple Ruins',
    hqLocationId: 'ancient-temple-ruins',
    description: 'Those who seek to harness the magic of the old world. Some pursue knowledge, others raw power. Valdris leads the most dangerous faction among them.',
    titles: {
      Friendly: 'Seeker of the Old Ways',
      Revered: 'Frostcrown Bearer',
    },
    classAffinity: {
      winter_witch: 5,
      runepriest: 3,
    },
  },
];

// ─── Lookup ─────────────────────────────────────────────────────

const factionMap = new Map<FactionId, FactionDefinition>(
  FACTIONS.map(f => [f.id, f]),
);

/** Get a faction by ID. */
export function getFaction(id: FactionId): FactionDefinition | undefined {
  return factionMap.get(id);
}

/** Get all factions. */
export function getAllFactions(): FactionDefinition[] {
  return [...FACTIONS];
}

// ─── Standing Calculation ───────────────────────────────────────

/** Reputation thresholds for standing tiers. */
const STANDING_THRESHOLDS: Array<{ min: number; standing: FactionStanding }> = [
  { min: 50, standing: 'Revered' },
  { min: 20, standing: 'Friendly' },
  { min: -19, standing: 'Neutral' },
  { min: -49, standing: 'Unfriendly' },
  { min: -100, standing: 'Hostile' },
];

/** Get the standing tier for a given reputation value (-100 to 100). */
export function getStanding(reputation: number): FactionStanding {
  for (const tier of STANDING_THRESHOLDS) {
    if (reputation >= tier.min) return tier.standing;
  }
  return 'Hostile';
}

/** Get the faction title for a given standing, if any. */
export function getFactionTitle(factionId: FactionId, standing: FactionStanding): string | undefined {
  return factionMap.get(factionId)?.titles[standing];
}
